import React from "react";
import { Link } from "react-router-dom";

function Section({ id, eyebrow, title, description, children }) {
  return (
    <section
      id={id}
      className="scroll-mt-24 rounded-[30px] border border-slate-200 bg-white p-6 shadow-[0_18px_55px_rgba(15,23,42,0.06)] sm:p-8"
    >
      {eyebrow ? (
        <div className="inline-flex rounded-full border border-[#E7D8AC] bg-[#FFF9EA] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#8A6A1F]">
          {eyebrow}
        </div>
      ) : null}

      <h2 className="mt-3 text-2xl font-semibold tracking-[-0.035em] text-slate-950">
        {title}
      </h2>

      {description ? (
        <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-600">
          {description}
        </p>
      ) : null}

      <div className="mt-6 space-y-5 text-sm leading-7 text-slate-600">
        {children}
      </div>
    </section>
  );
}

function Notice({ title, children, tone = "neutral" }) {
  const toneClass =
    tone === "warning"
      ? "border-[#E7D8AC] bg-[#FFF9EA] text-[#6F5318]"
      : tone === "danger"
        ? "border-rose-200 bg-rose-50 text-rose-900"
        : tone === "success"
          ? "border-emerald-200 bg-emerald-50 text-emerald-900"
          : "border-slate-200 bg-slate-50 text-slate-700";

  return (
    <div className={`rounded-2xl border p-4 ${toneClass}`}>
      <div className="text-sm font-semibold">{title}</div>
      <div className="mt-2 text-sm leading-6 opacity-90">{children}</div>
    </div>
  );
}

function Step({ number, title, children }) {
  return (
    <div className="flex gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#050713] text-sm font-semibold text-[#E6C978]">
        {number}
      </div>
      <div>
        <div className="text-sm font-semibold text-slate-950">{title}</div>
        <div className="mt-1 text-sm leading-6 text-slate-600">{children}</div>
      </div>
    </div>
  );
}

function CodeBlock({ children }) {
  return (
    <pre className="overflow-x-auto rounded-2xl border border-slate-200 bg-slate-950 p-4 text-xs leading-6 text-slate-100">
      <code>{children}</code>
    </pre>
  );
}

function QuickLink({ href, children }) {
  return (
    <a
      href={href}
      className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-[#E7D8AC] hover:bg-[#FFF9EA] hover:text-[#6F5318]"
    >
      {children}
    </a>
  );
}

export default function Guide() {
  return (
    <div className="space-y-8">
      <section className="relative overflow-hidden rounded-[34px] border border-[#1F2937] bg-[#050713] p-6 text-white shadow-[0_24px_80px_rgba(15,23,42,0.18)] sm:p-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(197,155,69,0.24),_transparent_30%),radial-gradient(circle_at_bottom_right,_rgba(255,255,255,0.08),_transparent_32%)]" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#E6C978]/60 to-transparent" />

        <div className="relative">
          <div className="inline-flex rounded-full border border-[#E6C978]/30 bg-[#E6C978]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#F8E7B0]">
            User Guide
          </div>

          <h1 className="mt-4 max-w-4xl text-3xl font-semibold leading-tight tracking-[-0.04em] sm:text-4xl">
            Prepare correctly before you deploy.
          </h1>

          <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-300">
            Learn what to check before deployment, how NFT and multi-token metadata works, how custom Solidity support works, and how to manage ownership, roles, dashboards, and refresh behavior after deployment.
          </p>

          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/studio"
              className="rounded-2xl bg-white px-4 py-3 text-sm font-semibold text-slate-950 transition hover:bg-[#FFF9EA]"
            >
              Open Studio
            </Link>

            <Link
              to="/help-center"
              className="rounded-2xl border border-white/15 bg-white/[0.06] px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
            >
              Contact Support
            </Link>
          </div>
        </div>
      </section>

      <section className="rounded-[30px] border border-slate-200 bg-white p-5 shadow-[0_18px_55px_rgba(15,23,42,0.06)] sm:p-6">
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8A6A1F]">
          Guide sections
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <QuickLink href="#before-deploy">Before deployment</QuickLink>
          <QuickLink href="#deployment-flow">Deployment flow</QuickLink>
          <QuickLink href="#erc20">ERC20 tokens</QuickLink>
          <QuickLink href="#erc721">ERC721 NFT metadata</QuickLink>
          <QuickLink href="#erc1155">ERC1155 multi-token metadata</QuickLink>
          <QuickLink href="#custom-solidity">Custom Solidity</QuickLink>
          <QuickLink href="#ownership-roles">Ownership and roles</QuickLink>
          <QuickLink href="#dashboard-refresh">Dashboard and Refresh</QuickLink>
          <QuickLink href="#mistakes">Common mistakes</QuickLink>
        </div>
      </section>

      <Section
        id="before-deploy"
        eyebrow="Before deployment"
        title="What to prepare before using Studio"
        description="A successful deployment depends on having the right contract settings, wallet, network, and metadata before you sign the transaction."
      >
        <div className="grid gap-4 md:grid-cols-2">
          <Notice title="Prepare these first" tone="success">
            <ul className="list-disc space-y-2 pl-5">
              <li>Correct wallet connected and signed in.</li>
              <li>Target network selected correctly.</li>
              <li>Enough native gas token in your wallet.</li>
              <li>Token name, symbol, supply, and owner settings reviewed.</li>
              <li>NFT or ERC1155 metadata uploaded before production deployment.</li>
            </ul>
          </Notice>

          <Notice title="Do not rush these" tone="warning">
            <ul className="list-disc space-y-2 pl-5">
              <li>Owner wallet address.</li>
              <li>Mintable, burnable, pausable, and role settings.</li>
              <li>Metadata base URI or ERC1155 URI pattern.</li>
              <li>Network selection, especially mainnet versus testnet.</li>
              <li>Wallet transaction prompts before signing.</li>
            </ul>
          </Notice>
        </div>

        <Notice title="Production rule">
          Test on a testnet first if you are unsure. Mainnet transactions are usually irreversible, and gas fees are paid directly from your wallet to the blockchain network.
        </Notice>
      </Section>

      <Section
        id="deployment-flow"
        eyebrow="Deployment flow"
        title="How deployment works"
        description="Use Studio to configure the contract, run checks, sign from your wallet, and then review the contract dashboard."
      >
        <div className="grid gap-4">
          <Step number="1" title="Choose contract type">
            Pick ERC20, ERC721, ERC1155, or use custom Solidity if you are deploying your own contract code.
          </Step>

          <Step number="2" title="Enter contract settings">
            Add name, symbol, supply, metadata URI, modules, royalty settings, owner address, and network details depending on the selected contract type.
          </Step>

          <Step number="3" title="Run preflight where available">
            Preflight helps catch configuration or transaction issues before deployment. It does not replace reviewing your own contract settings.
          </Step>

          <Step number="4" title="Sign with your wallet">
            Krionex prepares the transaction. Your wallet signs and broadcasts it. Your wallet pays gas directly to the blockchain network.
          </Step>

          <Step number="5" title="Review the dashboard">
            After deployment, use the dashboard to view verification status, owner state, history, supported actions, balances, and contract information.
          </Step>
        </div>
      </Section>

      <Section
        id="erc20"
        eyebrow="ERC20"
        title="Deploying ERC20 tokens"
        description="ERC20 is used for fungible tokens where every unit is the same."
      >
        <ul className="list-disc space-y-2 pl-5">
          <li>Use a clear token name and symbol. These are visible in wallets and explorers.</li>
          <li>Use 18 decimals unless your project has a specific reason to use another value.</li>
          <li>Set initial supply carefully. If you mint supply during deployment, it goes to the configured owner/recipient depending on the contract setup.</li>
          <li>Enable mintable only if you need future supply creation.</li>
          <li>Enable burnable only if token holders or authorized wallets should be able to destroy tokens.</li>
          <li>Enable pausable only if you need emergency pause controls and understand the trust implications.</li>
        </ul>

        <Notice title="ERC20 safety note" tone="warning">
          Tokenomics, supply, legal treatment, distribution, and community promises are your responsibility. Krionex only provides deployment and management tooling.
        </Notice>
      </Section>

      <Section
        id="erc721"
        eyebrow="ERC721"
        title="Preparing ERC721 NFT metadata"
        description="ERC721 is normally used for one-of-one NFT items where each token ID points to its own metadata JSON."
      >
        <p>
          Metadata is the JSON information that wallets, marketplaces, explorers, and apps use to display an NFT. It usually contains the NFT name, description, image link, and attributes.
        </p>

        <p>
          Krionex does not automatically create artwork or metadata JSON in this version. You should create the image files and JSON metadata first, upload them to IPFS, Arweave, or stable HTTPS storage, and then paste the metadata folder URI into Studio.
        </p>

        <Notice title="ERC721 Base URI example" tone="success">
          If your metadata folder contains 1.json, 2.json, and 3.json, your Base URI can be: <span className="font-semibold">ipfs://YOUR_METADATA_CID/</span>
        </Notice>

        <CodeBlock>{`Example folder:
ipfs://YOUR_METADATA_CID/1.json
ipfs://YOUR_METADATA_CID/2.json
ipfs://YOUR_METADATA_CID/3.json`}</CodeBlock>

        <p>Example ERC721 metadata JSON:</p>

        <CodeBlock>{`{
  "name": "Example NFT #1",
  "description": "A sample NFT item.",
  "image": "ipfs://YOUR_IMAGE_CID/1.png",
  "attributes": [
    { "trait_type": "Background", "value": "Gold" },
    { "trait_type": "Level", "value": "1" }
  ]
}`}</CodeBlock>

        <Notice title="Common ERC721 mistake" tone="danger">
          Do not paste a direct image URL as the Base URI. The Base URI should point to metadata JSON files, not only to PNG, JPG, GIF, or MP4 files.
        </Notice>
      </Section>

      <Section
        id="erc1155"
        eyebrow="ERC1155"
        title="Preparing ERC1155 multi-token metadata"
        description="ERC1155 is used when one contract manages multiple token IDs, such as editions, game items, access passes, or mixed assets."
      >
        <p>
          ERC1155 usually uses one URI pattern for many token IDs. The URI commonly contains {"{id}"}, and wallets or marketplaces replace {"{id}"} with the token ID being displayed.
        </p>

        <Notice title="ERC1155 URI example" tone="success">
          ipfs://YOUR_METADATA_CID/{"{id}"}.json
        </Notice>

        <p>
          Many ERC1155 readers expect the token ID to be converted into a 64-character lowercase hexadecimal file name.
        </p>

        <CodeBlock>{`Token ID: 1

Expected ERC1155 metadata file name:
0000000000000000000000000000000000000000000000000000000000000001.json`}</CodeBlock>

        <p>Example ERC1155 metadata JSON:</p>

        <CodeBlock>{`{
  "name": "Access Pass #1",
  "description": "A sample ERC1155 multi-token item.",
  "image": "ipfs://YOUR_IMAGE_CID/pass-1.png",
  "attributes": [
    { "trait_type": "Type", "value": "Access Pass" }
  ]
}`}</CodeBlock>

        <Notice title="ERC1155 production checklist" tone="warning">
          Confirm your metadata file names match the token IDs you plan to mint. If the file names do not match the ERC1155 URI pattern, wallets and marketplaces may show blank or missing metadata.
        </Notice>
      </Section>

      <Section
        id="custom-solidity"
        eyebrow="Custom Solidity"
        title="Using custom Solidity"
        description="Custom Solidity is for advanced users who want to deploy their own contract code instead of only using standard templates."
      >
        <p>
          With custom Solidity, you can compile, preflight, deploy, verify, and open a dashboard for supported custom contracts.
        </p>

        <div className="grid gap-4 md:grid-cols-2">
          <Notice title="What can work in Level 1" tone="success">
            <ul className="list-disc space-y-2 pl-5">
              <li>Compile custom Solidity.</li>
              <li>Deploy with your wallet.</li>
              <li>Request verification where supported.</li>
              <li>Detect owner() and transferOwnership where compatible.</li>
              <li>Detect selected AccessControl-style role actions where compatible.</li>
              <li>Show contract dashboard, history, verification, and ownership state.</li>
            </ul>
          </Notice>

          <Notice title="What is not guaranteed" tone="warning">
            <ul className="list-disc space-y-2 pl-5">
              <li>Every custom function will be detected.</li>
              <li>Every custom contract will behave like a token.</li>
              <li>Every ABI action will have a dashboard button in Level 1.</li>
              <li>Krionex cannot prove your custom business logic is safe.</li>
              <li>Krionex cannot guarantee marketplace/indexer support for unusual contracts.</li>
            </ul>
          </Notice>
        </div>

        <Notice title="Custom Solidity safety rule" tone="danger">
          Read your contract carefully and test on a testnet before mainnet. A successful compile does not mean the contract is safe, correct, or suitable for your project.
        </Notice>
      </Section>

      <Section
        id="ownership-roles"
        eyebrow="Ownership and roles"
        title="Owner wallets, role permissions, and action access"
        description="The dashboard only enables actions when the connected wallet appears authorized for the supported operation."
      >
        <p>
          For Ownable contracts, the live on-chain owner() value decides who controls owner-only functions. If ownership is transferred to another wallet, the previous owner can still view the dashboard but owner-only controls become disabled.
        </p>

        <p>
          For AccessControl contracts, permissions depend on roles such as minter or pauser roles where supported by the contract.
        </p>

        <div className="grid gap-4 md:grid-cols-2">
          <Notice title="Ownership transfer">
            Only transfer ownership to a wallet you control or fully trust. After transfer, that wallet controls owner-only functions.
          </Notice>

          <Notice title="Role actions">
            Grant roles only to trusted wallets. A wallet with a minter or pauser role may be able to perform powerful contract actions.
          </Notice>
        </div>
      </Section>

      <Section
        id="dashboard-refresh"
        eyebrow="Dashboard"
        title="Normal loading, action refresh, and manual Refresh"
        description="Krionex separates normal dashboard loading from heavier manual refresh behavior."
      >
        <div className="grid gap-4">
          <Step number="1" title="Normal dashboard open">
            Loads saved deployment metadata, verification state, history, account usage, and lightweight contract reads.
          </Step>

          <Step number="2" title="After a contract action">
            Reloads history, account usage, and the live contract state needed for that action, such as owner(), without forcing unnecessary analytics or liquidity refreshes.
          </Step>

          <Step number="3" title="Manual Refresh button">
            Re-checks live contract state and supported analytics where applicable. For custom non-token contracts, token analytics, liquidity, and NFT insights are not applicable.
          </Step>
        </div>
      </Section>

      <Section
        id="mistakes"
        eyebrow="Common mistakes"
        title="Common mistakes and how to avoid them"
        description="Check these before deploying or signing a transaction."
      >
        <div className="grid gap-4 md:grid-cols-2">
          <Notice title="Wrong network">
            Make sure you selected the intended network. Testnet and mainnet deployments are separate.
          </Notice>

          <Notice title="Wrong owner wallet">
            Confirm the owner address before deployment. The owner controls owner-only functions.
          </Notice>

          <Notice title="Placeholder metadata">
            Do not deploy production NFTs with YOUR_METADATA_CID unless you intentionally want placeholder metadata.
          </Notice>

          <Notice title="Direct image instead of JSON">
            NFT metadata should usually point to JSON files. The JSON then points to the image.
          </Notice>

          <Notice title="ERC1155 file name mismatch">
            For ERC1155, confirm whether your marketplace expects 64-character hexadecimal token ID file names.
          </Notice>

          <Notice title="Too much trust in custom code">
            Custom Solidity should be reviewed and tested before deployment. Krionex cannot guarantee custom logic safety.
          </Notice>
        </div>
      </Section>

      <Section
        id="support"
        eyebrow="Support"
        title="Need help with a deployment or issue?"
        description="Use Support Center for transaction, verification, billing, or app issues."
      >
        <p>
          When contacting support, include the network, contract address, transaction hash, wallet address, and a clear description of what happened.
        </p>

        <Link
          to="/help-center"
          className="inline-flex rounded-2xl bg-[#050713] px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-900"
        >
          Open Support Center
        </Link>
      </Section>
    </div>
  );
}